import {Component, ErrorInfo, ReactNode} from 'react';
import {connect} from 'react-redux';
import {Dispatch} from 'redux';
import {Text} from '../design-system/atoms/Text/Text';
import {REFRESH_TALK_VIEWER_ERROR_TYPE_LIST} from '../domain/content/[seriesId]/viewer/[productId]/constant/talkViewerConstant';
import {events} from '../domain/content/[seriesId]/viewer/[productId]/constant/talkViewerEvents';
import {clearTalkViewerError} from '../domain/content/[seriesId]/viewer/[productId]/state/talkViewerSlice';
import {AppState} from '../state/store';
import {getSerializedPagewebError} from './getSerializedPagewebError.function';
import {PagewebErrorType} from './PagewebErrorType.enum';
import {SerializedPagewebError} from './SerializedPagewebError.interface';

interface Props
  extends ReturnType<typeof mapDispatchToProps>,
    ReturnType<typeof mapStateToProps> {
  children: ReactNode;
}

interface State {
  shouldHandleError: boolean;
  talkViewerError?: SerializedPagewebError;
}

/**
 * 처리되지 않은 API Error 를 처리하는 바운더리입니다
 * 네트워크 연결 에러, 요청 처리 실패 등의 에러를 처리합니다
 * 페이지 전체 에러를 보여줘야하는 경우(예: 서버점검) 상위 ErrorBoundary 로 전달합니다
 */
class TalkViewerErrorBoundaryClass extends Component<Props, State> {
  public state: State = {
    shouldHandleError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    const serializedPagewebError = getSerializedPagewebError(error);
    if (
      serializedPagewebError.type === PagewebErrorType.TalkViewerError &&
      !!serializedPagewebError.talkViewerErrorType
    ) {
      return {
        shouldHandleError: true,
        talkViewerError: serializedPagewebError,
      };
    } else {
      return {
        shouldHandleError: false,
      };
    }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // TODO: 적절한 로깅 처리
    console.error('Uncaught error:', error, errorInfo);
  }

  public componentDidUpdate(prevProps: Props) {
    if (
      prevProps.talkViewerError &&
      this.state.shouldHandleError &&
      !this.props.talkViewerError
    ) {
      this.setState({shouldHandleError: false});
    }
  }

  public render() {
    if (
      !this.state.shouldHandleError ||
      !this.state.talkViewerError?.talkViewerErrorType
    ) {
      return this.props.children;
    }
    return (
      <div
        className="flex h-full w-full flex-col items-center justify-center bg-static-mono-10"
        onClick={this.props.onClickBackground}
      >
        <Text className="whitespace-pre-wrap text-center text-[14px] text-static-mono-40">
          {this.state.talkViewerError.message}
        </Text>
        {REFRESH_TALK_VIEWER_ERROR_TYPE_LIST.includes(
          this.state.talkViewerError.talkViewerErrorType
        ) && (
          <div className="mt-20pxr cursor-pointer">
            {/* eslint-disable-next-line jsx-a11y/alt-text */}
            <img
              className="h-40pxr w-40pxr"
              // 네트워크가 끊긴 상황에서도 볼 수 있도록 data-uri 로 변환함. (/static/common/btn-viewerend-refresh.svg 를 https://base64.guru/converter/encode/image/svg 에서 변환)
              src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI4MCIgaGVpZ2h0PSI4MCIgdmlld0JveD0iMCAwIDgwIDgwIj4KICAgIDxnIGZpbGw9Im5vbmUiIGZpbGwtcnVsZT0iZXZlbm9kZCI+CiAgICAgICAgPGc+CiAgICAgICAgICAgIDxjaXJjbGUgY3g9IjQwIiBjeT0iNDAiIHI9IjQwIiBmaWxsPSIjMDAwIiBmaWxsLW9wYWNpdHk9Ii4yNDEiLz4KICAgICAgICAgICAgPHBhdGggZmlsbD0iI0ZGRiIgZD0iTTIyLjkgMzIuMjI0bDIuNTQ4IDEuMjM0YzIuMzQ2LTUuNjM2IDcuODQtOS42MTIgMTQuMjU2LTkuNjEyIDguNTM2IDAgMTUuNDgxIDcuMDIzIDE1LjQ4MSAxNS42NTRTNDguMjQgNTUuMTU0IDM5LjcwNCA1NS4xNTRjLTYuNTQ5IDAtMTIuMTQ4LTQuMzQtMTQuNDA3LTEwLjE2MWg2LjI5OWMuMSAwIC4yLS4wMTEuMjk0LS4wMzIuNjM2LS4xMzcgMS4xMTMtLjgyNyAxLjExMy0xLjUxIDAtLjc4Ni0uNjMtMS40MjQtMS40MDctMS40MjRIMjEuNDA3Yy0uNzc2IDAtMS40MDcuNjM4LTEuNDA3IDEuNDI0djEwLjEwN2MwIC43ODYuNzU4IDEuNDI0IDEuNTM1IDEuNDI0Ljc3NiAwIDEuNDc4LS42MzggMS40NzgtMS40MjRsLS4xOTgtNi45N0MyNS41NyA1My4yODEgMzIuMDk5IDU4IDM5LjcwNCA1OCA0OS43OTIgNTggNTggNDkuNyA1OCAzOS41UzQ5Ljc5MiAyMSAzOS43MDQgMjFDMzIuMTcgMjEgMjUuNyAyNS42MzQgMjIuOSAzMi4yMjR6Ii8+CiAgICAgICAgPC9nPgogICAgPC9nPgo8L3N2Zz4K"
              onClick={() => {
                this.setState({shouldHandleError: false});
                this.props.onClickReload();
              }}
            />
          </div>
        )}
      </div>
    );
  }
}
const mapStateToProps = (state: AppState) => {
  return {
    talkViewerError: state.talkViewer.error,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  onClickReload: () => dispatch(clearTalkViewerError()),
  onClickBackground: () => dispatch(events.viewerMenuExposedToggleTapPressed()),
});

export const TalkViewerErrorBoundary = connect(
  mapStateToProps,
  mapDispatchToProps
)(TalkViewerErrorBoundaryClass);
